import React, { useState } from "react";
import axios from "axios";
import { API_URL } from "src/scenes/App";
import {
    LottieLoadingIndicator,
    Toast,
    StandardModal,
    StandardModalActionButton,
    StandardModalActionGroup,
    StandardModalFormGroup,
    StandardModalInput,
    StandardModalLabel,
    StandardModalSection,
    StandardModalState,
    StandardModalText
} from "src/components";

function AccountDeletion({ token, open, handleClose, deletionType = "employee" }) {
    const [confirmationText, setConfirmationText] = useState("");
    const [isSuccess, setIsSuccess] = useState(false);
    const [loading, setLoading] = useState(false);

    const handleConfirm = async() => {
        if (confirmationText !== "DELETE") {
            return;
        }

        setLoading(true);
        try {
            const endpoint = deletionType === "account_owner" ? `${API_URL}/company/deleteDataRequest` : `${API_URL}/company/employee/deleteDataRequest`;

            const response = await axios.post(endpoint, {}, {
                headers: { Authorization: `Bearer ${token}` },
            });

            if (response.data?.status === "pending") {
                Toast.info("You already requested an account deletion. Please wait for the confirmation email.");
                return;
            }

            await new Promise((resolve) => setTimeout(resolve, 500));
            setIsSuccess(true);
        } catch (err) {
            console.error(err);
            Toast.error("Failed to request account deletion. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const handleDialogClose = () => {
        setIsSuccess(false);
        setConfirmationText(""); 
        handleClose(); 
    };

    const title = isSuccess
        ? "Account Deletion Request - Final Confirmation"
        : "Confirm Account Deletion";
    const footer = loading ? null : isSuccess ? (
        <StandardModalActionGroup $center justifyContent="center">
            <StandardModalActionButton onClick={handleDialogClose}>
                Close
            </StandardModalActionButton>
        </StandardModalActionGroup>
    ) : (
        <StandardModalActionGroup $pushRight>
            <StandardModalActionButton onClick={handleDialogClose}>
                Cancel
            </StandardModalActionButton>
            <StandardModalActionButton
                $variant="danger"
                disabled={confirmationText !== "DELETE"}
                width="10rem"
                minWidth="10rem"
                onClick={handleConfirm}
            >
                Confirm deletion
            </StandardModalActionButton>
        </StandardModalActionGroup>
    );

    return (
        <StandardModal
            open={open}
            onClose={handleDialogClose}
            title={title}
            widthMode="large"
            footer={footer}
        >
            {loading ? (
                <StandardModalState minHeight="14rem">
                    <LottieLoadingIndicator height="4em" width="4em" />
                </StandardModalState>
            ) : 
                isSuccess ? (
                    <StandardModalSection gap="0.85rem">
                        <StandardModalText>
                            Your request to permanently delete your account and all associated data has been received.
                        </StandardModalText>
                        <StandardModalText weight="700" color="#171A22">
                            This process is irreversible and will result in:
                        </StandardModalText>
                        <StandardModalText as="ul" margin="0 0 0 1.15rem">
                            <li>Permanent deletion of all account data, stored content, and user information</li>
                            <li>Cancellation of any active subscriptions</li>
                            <li>Revocation of all user access linked to this account</li>
                        </StandardModalText>
                        <StandardModalText>
                            Deletion requests are processed securely and may take up to 24 hours to complete. You will receive a final confirmation email once the deletion has been fully processed.
                        </StandardModalText>
                        <StandardModalText>
                            If this request was made in error, please contact support@mebusiness.com.au immediately. Restoration is not guaranteed once processing has begun.
                        </StandardModalText>
                    </StandardModalSection>
                ) : (
                    <StandardModalSection gap="0.85rem">
                        <StandardModalText weight="700" color="#D64545">
                            Warning: This action is irreversible.
                        </StandardModalText>
                        <StandardModalText>
                            Deleting your account will permanently remove all business and user data associated with this account. This includes:
                        </StandardModalText>
                        <StandardModalText as="ul" margin="0 0 0 1.15rem">
                            <li>All user profiles, messages, and stored content</li>
                            <li>Business settings, licenses, and subscription details</li>
                            <li>Access to all linked apps and features</li>
                        </StandardModalText>
                        <StandardModalText>
                            Once deleted, this data cannot be recovered, and any active subscriptions will be immediately cancelled.
                        </StandardModalText>
                        <StandardModalText>
                            If you understand and wish to proceed, please confirm by typing <strong>DELETE</strong> in the field below.
                        </StandardModalText>
                        <StandardModalFormGroup>
                            <StandardModalLabel>Type DELETE to confirm:</StandardModalLabel>
                            <StandardModalInput
                                type="text"
                                placeholder="Type DELETE"
                                onChange={(e) => setConfirmationText(e.target.value)}
                                value={confirmationText}
                                maxLength="100"
                            />
                        </StandardModalFormGroup>
                    </StandardModalSection>
                )}
        </StandardModal>
    );
}

export default AccountDeletion;
